/**
 * Reading an RFC 9457 problem out of the generated client's `ApiError`.
 *
 * `API-Spec.md#conventions` fixes the shape and says what the `code` is for:
 *
 * > `code` is a stable machine-readable string. The UI maps codes to explanations and
 * > knowledge-base links, so an error is a teaching moment rather than a dead end.
 *
 * That mapping starts here. The rule this module exists to enforce is that **the UI never
 * matches on a status code or on message text** — a 401 is `auth.not_authenticated` or
 * `auth.session_expired` and those want different sentences, while an HTTP status cannot
 * tell them apart and a message string changes the day somebody improves the wording.
 *
 * `ApiError.body` is `unknown` in the generated types, so everything here is written to
 * survive a body that is not a problem document at all — a proxy's HTML error page, a
 * truncated response. A helper that threw while rendering an error message would replace a
 * useful failure with a useless one.
 */

import { ApiError } from './generated';

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  code?: string;
  detail?: string;
  request_id?: string | null;
}

export function problemOf(error: unknown): Problem | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body = error.body;
  if (typeof body !== 'object' || body === null) return undefined;
  return body as Problem;
}

export function codeOf(error: unknown): string | undefined {
  return problemOf(error)?.code;
}

export function statusOf(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined;
}

/**
 * Whether this error means "there is no usable session".
 *
 * Both codes, and a bare 401 as the backstop: a 401 from something that did not render a
 * problem body — a reverse proxy, a future middleware — still means the same thing, and
 * failing to notice it would leave the app showing a signed-in shell that cannot load
 * anything.
 */
export function isUnauthenticated(error: unknown): boolean {
  const code = codeOf(error);
  if (code === 'auth.not_authenticated' || code === 'auth.session_expired') return true;
  return statusOf(error) === 401;
}

/** True when the session was valid and has ended, as opposed to never having existed. */
export function isSessionExpired(error: unknown): boolean {
  return codeOf(error) === 'auth.session_expired';
}

/**
 * A sentence to show the operator. The problem's own `detail`, then its `title`.
 *
 * The server writes both deliberately — `problems.py` keeps authentication failures
 * uninformative on purpose and makes exchange failures specific on purpose — so preferring
 * the server's words over anything invented here is what keeps that decision in one place.
 */
export function messageOf(error: unknown, fallback: string): string {
  const problem = problemOf(error);
  if (problem?.detail) return problem.detail;
  if (problem?.title) return problem.title;
  if (error instanceof ApiError && error.statusText) return error.statusText;
  return fallback;
}

/** The `request_id`, so an operator reading a failure can find it in the service log. */
export function requestIdOf(error: unknown): string | undefined {
  return problemOf(error)?.request_id ?? undefined;
}
